import React, { useState, useEffect, useRef } from "react";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";

const ChatContainer = ({ onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState("");
  const [answeredOptionMessages, setAnsweredOptionMessages] = useState(
    new Set()
  );
  const messagesEndRef = useRef(null);
  const otherInputRef = useRef(null);

  console.log("userResponses :: ", userResponses);
  // Job seeker onboarding questions - Employment Details
  const questions = [
    {
      id: "work_status",
      question:
        "Hi! I'm here to help you complete your profile. Let's start with your employment details. Are you an experienced professional or a fresher?",
      type: "select",
      category: "employment",
      options: ["Experienced", "Fresher"],
    },
    {
      id: "currently_employed",
      question: "Are you currently employed?",
      type: "select",
      category: "employment",
      options: ["Yes", "No"],
    },
    {
      id: "total_experience",
      question: "How many years of total work experience do you have?",
      type: "text",
      category: "employment",
      placeholder: "e.g., 3.5 years",
    },
    {
      id: "current_company_name",
      question: "What is the name of your current company?",
      type: "text",
      category: "employment",
      placeholder: "Enter company name",
    },
    {
      id: "current_position",
      question: "What is your current position/designation in the company?",
      type: "text",
      category: "employment",
      placeholder: "e.g., Software Engineer, Marketing Manager",
    },
    {
      id: "start_date",
      question:
        "When did you start working at your current company? (Month, Year)",
      type: "text",
      category: "employment",
      placeholder: "e.g., January 2022",
    },
    {
      id: "end_date",
      question:
        "What is your current employment status regarding leaving your company?",
      type: "select",
      category: "employment",
      options: [
        "Immediately joining",
        "Currently serving notice period",
        "Other",
      ],
      placeholder:
        "e.g., Last working day - March 2024, Available from April 2024",
    },
    {
      id: "current_city",
      question: "Which city are you currently based in?",
      type: "text",
      category: "employment",
      placeholder: "Enter your current city",
    },
    {
      id: "notice_period",
      question: "What is your notice period?",
      type: "select",
      category: "employment",
      options: [
        "Less than 30 days",
        "30 days",
        "60 days",
        "90 days",
        "90 days+",
        "Serving notice period",
        "Immediate joiner",
      ],
    },
    {
      id: "annual_salary",
      question:
        "What is your current annual salary? (You can skip this if you prefer not to share)",
      type: "text",
      category: "employment",
      optional: true,
      placeholder: "e.g., 8 LPA or 800,000",
    },
    // Education Details
    {
      id: "highest_qualification",
      question:
        "Great! Now let's talk about your education. What is your highest qualification?",
      type: "select",
      category: "education",
      options: [
        "PhD",
        "Post graduation",
        "Graduation/Diploma",
        "12th",
        "10th",
        "Below 10th",
      ],
    },
    {
      id: "course_name",
      question: "What is the name of your course/degree?",
      type: "text",
      category: "education",
      placeholder:
        "e.g., Bachelor of Technology, Master of Business Administration",
    },
    {
      id: "course_type",
      question: "What type of course was this?",
      type: "select",
      category: "education",
      options: ["Full time", "Part time", "Distance education"],
    },
    {
      id: "specialization",
      question: "What was your specialization or major subject?",
      type: "text",
      category: "education",
      placeholder: "e.g., Computer Science, Marketing, Mechanical Engineering",
    },
    {
      id: "starting_year",
      question: "In which year did you start this course?",
      type: "text",
      category: "education",
      placeholder: "e.g., 2018",
    },
    {
      id: "passing_year",
      question: "In which year did you complete/pass this course?",
      type: "text",
      category: "education",
      placeholder: "e.g., 2022",
    },
    {
      id: "grade",
      question: "What grade/percentage/CGPA did you achieve?",
      type: "text",
      category: "education",
      placeholder: "e.g., 8.5 CGPA, 85%, First Class",
    },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start the conversation
    if (messages.length === 0) {
      setTimeout(() => {
        addBotMessage(questions[0].question);
        if (questions[0].options) {
          addOptionsMessage(questions[0].options);
        }
      }, 1000);
    }
  }, []);

  useEffect(() => {
    // Focus on the "Other" input when it appears
    if (showOtherInput && otherInputRef.current) {
      setTimeout(() => {
        otherInputRef.current.focus();
      }, 100);
    }
  }, [showOtherInput]);

  const addBotMessage = (message) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          message,
          isBot: true,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
      setIsTyping(false);
    }, 1500);
  };

  const addOptionsMessage = (options) => {
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          message: "",
          isBot: true,
          isOptions: true,
          options,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    }, 2000);
  };

  const addUserMessage = (message) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        message,
        isBot: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
  };

  const handleUserResponse = (response) => {
    if (isWaitingForResponse) return;

    setIsWaitingForResponse(true);
    addUserMessage(response);

    // Store the response
    const currentQuestion = questions[currentQuestionIndex];

    setUserResponses((prev) => {
      if (currentQuestion.category === "employment") {
        // Store employment details as an object
        return {
          ...prev,
          employment: {
            ...prev.employment,
            [currentQuestion.id]: response,
          },
        };
      } else if (currentQuestion.category === "education") {
        // Store education details as an object
        return {
          ...prev,
          education: {
            ...prev.education,
            [currentQuestion.id]: response,
          },
        };
      } else {
        // Store other responses directly
        return {
          ...prev,
          [currentQuestion.id]: response,
        };
      }
    });

    // Move to next question or complete
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        const nextQuestion = questions[nextIndex];

        addBotMessage(nextQuestion.question);
        if (nextQuestion.options) {
          addOptionsMessage(nextQuestion.options);
        }
      } else {
        // Onboarding complete
        addBotMessage(
          "Excellent! You've completed your profile setup with employment and education details. Your information will help us match you with the best job opportunities!"
        );
        setTimeout(() => {
          onComplete && onComplete(userResponses);
        }, 2000);
      }
      setIsWaitingForResponse(false);
    }, 1000);
  };

  const handleOptionSelect = (option, messageId) => {
    // Mark this option message as answered
    setAnsweredOptionMessages((prev) => new Set([...prev, messageId]));

    if (option === "Other") {
      // Show text input for "Other" option
      setShowOtherInput(true);
      addUserMessage(option);
    } else {
      handleUserResponse(option);
    }
  };

  const handleOtherSubmit = () => {
    if (otherInputValue.trim()) {
      setShowOtherInput(false);
      handleUserResponse(otherInputValue.trim());
      setOtherInputValue("");
    }
  };

  const renderMessage = (msg) => {
    if (msg.isOptions) {
      const isAnswered = answeredOptionMessages.has(msg.id);
      return (
        <div key={msg.id} className="flex justify-start mb-4">
          <div className="max-w-xs lg:max-w-md">
            <div className="grid grid-cols-1 gap-2">
              {msg.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(option, msg.id)}
                  disabled={isWaitingForResponse || isAnswered}
                  className={`text-left p-3 rounded-lg transition-colors duration-200 ${
                    isAnswered
                      ? "bg-gray-100 border border-gray-300 text-gray-500 cursor-not-allowed opacity-75"
                      : "bg-white border border-[#059669] text-[#059669] hover:bg-gray-50 hover:border-[#047857]"
                  } ${isWaitingForResponse || isAnswered ? "opacity-50" : ""}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <ChatMessage
        key={msg.id}
        message={msg.message}
        isBot={msg.isBot}
        timestamp={msg.timestamp}
      />
    );
  };

  const getCurrentQuestion = () => questions[currentQuestionIndex];

  // Check if there are currently unanswered options
  const hasUnansweredOptions = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion?.options) return false;

    // Find the most recent options message
    const optionsMessages = messages.filter((msg) => msg.isOptions);
    if (optionsMessages.length === 0) return false;

    const latestOptionsMessage = optionsMessages[optionsMessages.length - 1];
    return !answeredOptionMessages.has(latestOptionsMessage.id);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Complete Your Profile
        </h2>
        <p className="text-sm text-gray-600">
          Question {currentQuestionIndex + 1} of {questions.length}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div
            className="bg-[#059669] h-2 rounded-full transition-all duration-300"
            style={{
              width: `${
                ((currentQuestionIndex + 1) / questions.length) * 100
              }%`,
            }}
          ></div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {messages.map(renderMessage)}

        {showOtherInput && (
          <div className="flex justify-start mb-4">
            <div className="max-w-xs lg:max-w-md w-full">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Please specify your answer:
                </p>
                <input
                  ref={otherInputRef}
                  type="text"
                  value={otherInputValue}
                  onChange={(e) => setOtherInputValue(e.target.value)}
                  placeholder={
                    getCurrentQuestion()?.placeholder || "Enter your answer..."
                  }
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#059669] mb-3"
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && otherInputValue.trim()) {
                      handleOtherSubmit();
                    }
                  }}
                />
                <button
                  onClick={handleOtherSubmit}
                  disabled={!otherInputValue.trim()}
                  className="w-full bg-[#059669] text-white py-2 px-4 rounded-md hover:bg-[#047857] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {isTyping && (
          <div className="flex justify-start mb-4">
            <div className="max-w-xs lg:max-w-md px-4 py-2 bg-gray-100 text-gray-800 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Always visible */}
      <ChatInput
        onSendMessage={handleUserResponse}
        disabled={
          isWaitingForResponse ||
          showOtherInput ||
          hasUnansweredOptions() ||
          isTyping
        }
        placeholder={
          isTyping
            ? "Assistant is typing..."
            : hasUnansweredOptions()
            ? "Please select an option above..."
            : showOtherInput
            ? "Please use the input field above..."
            : getCurrentQuestion()?.placeholder ||
              (getCurrentQuestion()?.optional
                ? "Type your answer (optional)..."
                : "Type your answer...")
        }
      />
    </div>
  );
};

export default ChatContainer;
